import { ArrowLeftIcon, BeakerIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAnalyse, useCase, useEnrich } from '../api/hooks';
import { SOURCE_LABELS } from '../api/types';
import type { Alert, Decision } from '../api/types';
import { Role } from '../auth/roles';
import { usePrincipal } from '../auth/usePrincipal';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card, CardHeader } from '../components/Card';
import { PageHeader } from '../components/Layout';
import { EmptyState, QueryBoundary } from '../components/states';
import {
  SeverityBadge,
  SourceBadge,
  StatusBadge,
  VerdictBadge,
} from '../components/DomainBadges';
import { useFormatters } from '../time/format';
import { ActionRow } from './partials/ActionRow';
import { EvidencePanel } from './partials/EvidencePanel';

export function CaseDetail() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const principal = usePrincipal();
  const query = useCase(caseId);
  const enrich = useEnrich(caseId);
  const analyse = useAnalyse(caseId);
  const { dateTime, precise, relative } = useFormatters();
  const [tab, setTab] = useState<'overview' | 'evidence'>('overview');

  const detail = query.data;
  const latestDecision = detail?.decisions?.[0];
  const canAnalyse = principal.role >= Role.ANALYST;

  return (
    <>
      <Link
        to="/cases"
        className="mb-3 inline-flex items-center gap-1 text-xs text-muted hover:text-body"
      >
        <ArrowLeftIcon className="h-3 w-3" aria-hidden="true" />
        All cases
      </Link>

      <QueryBoundary
        isLoading={query.isPending}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Loading case"
      >
        {!detail ? (
          <EmptyState title="Case not found" detail={caseId} />
        ) : (
          <>
            <PageHeader
              title={detail.case.title}
              subtitle={`${detail.case.case_id} · opened ${dateTime(detail.case.opened_at)} · updated ${relative(detail.case.updated_at)}`}
              actions={
                canAnalyse ? (
                  <>
                    <Button
                      size="xs"
                      onClick={() => enrich.mutate()}
                      disabled={enrich.isPending}
                      title="Re-run the IR collectors and Graph enrichment for this case"
                    >
                      <BeakerIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {enrich.isPending ? 'Enriching…' : 'Re-enrich'}
                    </Button>
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => analyse.mutate()}
                      disabled={analyse.isPending}
                      title="Send the current evidence pack to the analyst model again"
                    >
                      <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {analyse.isPending ? 'Analysing…' : 'Re-analyse'}
                    </Button>
                  </>
                ) : undefined
              }
            />

            <div className="mb-4 flex flex-wrap items-center gap-2">
              <SeverityBadge severity={detail.case.severity} />
              <StatusBadge status={detail.case.status} />
              {detail.case.assigned_to ? (
                <Badge tone="violet">Assigned to {detail.case.assigned_to}</Badge>
              ) : null}
              {detail.case.closed_reason ? (
                <Badge tone="neutral">{detail.case.closed_reason}</Badge>
              ) : null}
            </div>

            {enrich.error ? <Callout tone="danger" role="alert">{enrich.error.message}</Callout> : null}
            {analyse.error ? (
              <Callout tone="danger" role="alert">{analyse.error.message}</Callout>
            ) : null}

            <div className="mb-5 flex gap-1 border-b border-line">
              <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
                Overview
              </TabButton>
              <TabButton active={tab === 'evidence'} onClick={() => setTab('evidence')}>
                Evidence
              </TabButton>
            </div>

            {tab === 'evidence' ? (
              <EvidencePanel caseId={caseId} />
            ) : (
              <div className="space-y-6">
                {latestDecision ? (
                  <DecisionCard decision={latestDecision} />
                ) : (
                  <Card>
                    <EmptyState
                      title="No analysis yet"
                      detail="The analyst runs once the evidence pack is assembled. Nothing has been recommended for this case."
                    />
                  </Card>
                )}

                <Card>
                  <CardHeader
                    title="Containment actions"
                    subtitle="What policy decided, and where each action got to"
                  />
                  {detail.actions.length === 0 ? (
                    <EmptyState
                      title="No actions"
                      detail="Either nothing was recommended, or every recommendation is still with the policy gate."
                    />
                  ) : (
                    <ul className="divide-y divide-line">
                      {detail.actions.map((action) => (
                        <ActionRow
                          key={action.action_id}
                          action={action}
                          caseId={caseId}
                          caseSubjects={detail.case.correlation_keys}
                        />
                      ))}
                    </ul>
                  )}
                </Card>

                <Card>
                  <CardHeader
                    title={`Alerts (${detail.alerts.length})`}
                    subtitle="Correlated on overlapping subjects, not on a time window alone"
                  />
                  {detail.alerts.length === 0 ? (
                    <EmptyState title="No alerts attached" />
                  ) : (
                    <ul className="divide-y divide-line">
                      {detail.alerts.map((alert) => (
                        <AlertRow key={alert.alert_id} alert={alert} format={precise} />
                      ))}
                    </ul>
                  )}
                </Card>

                {detail.decisions.length > 1 ? (
                  <Card>
                    <CardHeader
                      title="Earlier analyses"
                      subtitle="Prior decisions are kept — a re-analysis does not replace the record"
                    />
                    <ul className="divide-y divide-line">
                      {detail.decisions.slice(1).map((decision) => (
                        <li
                          key={decision.decision_id}
                          className="flex flex-wrap items-center gap-2 py-3"
                        >
                          <VerdictBadge verdict={decision.verdict} />
                          <span className="text-sm text-muted">{decision.summary}</span>
                          <span className="ml-auto text-xs text-subtle">
                            {precise(decision.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
              </div>
            )}
          </>
        )}
      </QueryBoundary>
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-brand text-brand-text'
          : 'border-transparent text-muted hover:text-body'
      }`}
    >
      {children}
    </button>
  );
}

function DecisionCard({ decision }: { decision: Decision }) {
  const { precise } = useFormatters();
  const confidence = Number.parseFloat(decision.confidence);

  return (
    <Card>
      <CardHeader
        title="Analyst decision"
        subtitle={`${decision.model_id || 'model'} · ${precise(decision.created_at)}`}
        action={<VerdictBadge verdict={decision.verdict} />}
      />

      <div className="space-y-4">
        {/*
          Instruction-shaped text inside evidence. Reported, never followed — and a non-empty
          value is itself a security signal, because evidence fields are adversary-
          influenceable and somebody trying to steer the triage pipeline is notable in its own
          right. It outranks the verdict on this page for that reason.
        */}
        {decision.injection_observed.length > 0 ? (
          <Callout tone="danger" role="alert">
            <p className="font-medium">
              Prompt-injection attempt found in evidence ({decision.injection_observed.length})
            </p>
            <p className="mt-0.5 opacity-90">
              Instruction-shaped text was present in collected evidence. It was reported to the
              analyst as a finding and not acted on. Treat this case as adversary-aware.
            </p>
            <ul className="mt-2 space-y-1">
              {decision.injection_observed.map((marker, index) => (
                <li
                  key={`${marker}-${index}`}
                  className="break-all rounded bg-overlay/20 px-2 py-1 font-mono text-[11px]"
                >
                  {marker}
                </li>
              ))}
            </ul>
          </Callout>
        ) : null}

        {!decision.evidence_complete ? (
          <Callout tone="warning">
            <p className="font-medium">Evidence pack is incomplete</p>
            <p className="mt-0.5 opacity-90">
              Some collection failed or was truncated. While{' '}
              <code className="font-mono">requireCompleteEvidenceForAuto</code> is set, this
              forces every action onto the approval path regardless of its configured mode.
            </p>
          </Callout>
        ) : null}

        {decision.verdict === 'insufficient_evidence' ? (
          <Callout tone="neutral">
            <p className="font-medium">Too incomplete to judge</p>
            <p className="mt-0.5 opacity-90">
              This is distinct from &ldquo;suspicious&rdquo;: it says the collection failed, not
              that the activity is ambiguous. It never leads to containment — any action with a
              blast radius is blocked on this verdict alone.
            </p>
          </Callout>
        ) : null}

        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">
            Summary
          </h3>
          <p className="text-sm text-body">{decision.summary}</p>
        </div>

        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">
            Reasoning
          </h3>
          <p className="whitespace-pre-wrap text-sm text-muted">{decision.reasoning}</p>
        </div>

        {decision.key_evidence.length > 0 ? (
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">
              Key evidence
            </h3>
            <ul className="list-inside list-disc space-y-1 text-sm text-muted">
              {decision.key_evidence.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {decision.intel_gaps.length > 0 ? (
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">
              Intel gaps
            </h3>
            <p className="mb-1 text-xs text-subtle">
              What the analyst wanted and did not have.
            </p>
            <ul className="list-inside list-disc space-y-1 text-sm text-muted">
              {decision.intel_gaps.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-line pt-3 text-xs text-subtle">
          <span>
            Assessed severity: <SeverityBadge severity={decision.assessed_severity} />
          </span>
          {/*
            Shown, and labelled for what it is. Confidence does not unlock anything: an
            action's mode comes from configuration and its guards from policy, so a model
            claiming 0.99 is treated exactly like one claiming 0.6. Displaying it without
            this caveat is how it quietly becomes the security control.
          */}
          <span title="Display and reporting only. Confidence is never an authorisation threshold.">
            Confidence:{' '}
            <span className="tabular-nums text-muted">
              {Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : '—'}
            </span>{' '}
            <span className="opacity-75">(not an authority)</span>
          </span>
          <span className="tabular-nums">
            Tokens: {decision.input_tokens.toLocaleString()} in ·{' '}
            {decision.output_tokens.toLocaleString()} out ·{' '}
            {decision.cached_tokens.toLocaleString()} cached
          </span>
        </div>
      </div>
    </Card>
  );
}

function AlertRow({
  alert,
  format,
}: {
  alert: Alert;
  format: (value: string) => string;
}) {
  return (
    <li className="py-3">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <SourceBadge source={alert.source} />
        <SeverityBadge severity={alert.severity} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-body">
          {alert.title}
        </span>
        {alert.source_url ? (
          <a
            href={alert.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-brand-text hover:underline"
            title={`Open in ${SOURCE_LABELS[alert.source] ?? alert.source}`}
          >
            Open in source
          </a>
        ) : null}
      </div>
      {alert.description ? (
        <p className="mb-1 text-xs text-muted">{alert.description}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        {alert.subjects.map((subject) => (
          <Badge key={`${subject.kind}:${subject.value}`} tone="neutral" title={subject.kind}>
            <span className="opacity-70">{subject.kind}</span>
            {subject.value}
          </Badge>
        ))}
        {alert.tactics.map((tactic) => (
          <Badge key={tactic} tone="purple" title="MITRE ATT&CK tactic">
            {tactic}
          </Badge>
        ))}
      </div>
      <p className="mt-1 text-[11px] tabular-nums text-subtle">
        Occurred {format(alert.occurred_at)} · received {format(alert.received_at)}
      </p>
    </li>
  );
}
